// File: frontend/src/components/BulkTriage.tsx
// Bulk IOC triage: paste a list of IOCs (defanged accepted) and check which
// ones are known to the corpus. Results can be exported as CSV.
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Paper,
    Typography,
    TextField,
    Button,
    Stack,
    Chip,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    alpha,
} from '@mui/material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';

interface TriageMatch {
    article_id: string;
    article_title: string | null;
    article_source: string | null;
    indicator_type: string | null;
    threat_actor: string | null;
    malware_family: string | null;
    first_seen: string | null;
}

interface TriageResult {
    submitted: string;
    normalized: string;
    found: boolean;
    match_count: number;
    matches: TriageMatch[];
}

interface TriageResponse {
    total_submitted: number;
    total_found: number;
    total_not_found: number;
    results: TriageResult[];
}

const MAX_IOCS = 500;

const BulkTriage: React.FC = () => {
    const { poster } = useApi();
    const [input, setInput] = useState('');
    const [response, setResponse] = useState<TriageResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const parseInput = (): string[] =>
        input
            .split(/[\n,;]+/)
            .map((v) => v.trim())
            .filter(Boolean)
            .slice(0, MAX_IOCS);

    const handleTriage = async () => {
        const iocs = parseInput();
        if (iocs.length === 0) return;
        setIsLoading(true);
        setError(null);
        setResponse(null);
        try {
            const data = await poster('/api/iocs/triage', { iocs });
            setResponse(data);
        } catch (err: any) {
            setError(err.message || 'Triage failed.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadCsv = () => {
        if (!response) return;
        const header = 'submitted,normalized,found,match_count,first_article_title,first_article_source,threat_actor,malware_family\n';
        const rows = response.results.map((r) => {
            const m = r.matches[0];
            const esc = (v: string | null | undefined) => `"${(v || '').replace(/"/g, '""')}"`;
            return [esc(r.submitted), esc(r.normalized), r.found, r.match_count, esc(m?.article_title), esc(m?.article_source), esc(m?.threat_actor), esc(m?.malware_family)].join(',');
        });
        const blob = new Blob([header + rows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ioc-triage-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const iocCount = parseInput().length;

    return (
        <Paper
            sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                border: `1px solid ${alpha(cyber.accent.cyan, 0.25)}`,
                background: alpha(cyber.accent.cyan, 0.03),
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <PlaylistAddCheckIcon sx={{ color: cyber.accent.cyan, fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                    Bulk IOC Triage
                </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste a list of IOCs from an alert or incident (one per line — defanged values like
                evil[.]com or hxxp:// are accepted) and check which ones are known to our corpus.
                Up to {MAX_IOCS} indicators per run.
            </Typography>

            <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={'185.220.101.1\nevil[.]com\nhxxps://malicious[.]site/payload\nd41d8cd98f00b204e9800998ecf8427e'}
                sx={{ mb: 2, '& textarea': { fontFamily: cyber.fontMono, fontSize: '0.85rem' } }}
            />

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                    variant="contained"
                    onClick={handleTriage}
                    disabled={isLoading || iocCount === 0}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddCheckIcon />}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                    {isLoading ? 'Checking…' : `Triage ${iocCount || ''} IOC${iocCount === 1 ? '' : 's'}`}
                </Button>
                {response && (
                    <>
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                            label={`${response.total_found} known`}
                            sx={{ bgcolor: alpha('#66bb6a', 0.15), color: '#66bb6a', fontWeight: 'bold', '& .MuiChip-icon': { color: '#66bb6a' } }}
                        />
                        <Chip
                            icon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
                            label={`${response.total_not_found} unknown`}
                            sx={{ bgcolor: alpha('#9fa4c0', 0.15), color: '#9fa4c0', fontWeight: 'bold', '& .MuiChip-icon': { color: '#9fa4c0' } }}
                        />
                        <Button size="small" onClick={handleDownloadCsv} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
                            Download results CSV
                        </Button>
                    </>
                )}
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {response && response.results.length > 0 && (
                <TableContainer sx={{ mt: 2, maxHeight: 420, border: `1px solid ${alpha(cyber.primary, 0.15)}`, borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>IOC</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Verdict</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Context</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Seen In</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {response.results.map((r, idx) => {
                                const m = r.matches[0];
                                return (
                                    <TableRow key={idx} sx={{ bgcolor: r.found ? alpha('#66bb6a', 0.04) : undefined }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontSize: '0.78rem', wordBreak: 'break-all' }}>
                                                {r.submitted}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {r.found ? (
                                                <Chip label={`Known (${r.match_count})`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha('#66bb6a', 0.15), color: '#66bb6a', fontWeight: 'bold' }} />
                                            ) : (
                                                <Chip label="Unknown" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha('#9fa4c0', 0.12), color: '#9fa4c0' }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {m?.threat_actor && (
                                                    <Chip label={m.threat_actor} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                )}
                                                {m?.malware_family && (
                                                    <Chip label={m.malware_family} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {m && (
                                                <Link component={RouterLink} to={`/report/${m.article_id}`} sx={{ fontSize: '0.78rem' }}>
                                                    {m.article_title || m.article_id}
                                                </Link>
                                            )}
                                            {r.match_count > 1 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                    +{r.match_count - 1} more
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default BulkTriage;
